import React, { createContext, useContext, useState, ReactNode } from 'react';
import { persistKeyForSession, restoreKeyFromSession, clearSessionKey } from '@mysecureroom/crypto';

interface CryptoState {
  encryptionKey: Uint8Array | null;
  isUnlocked: boolean;
}

interface CryptoContextValue extends CryptoState {
  setEncryptionKey: (key: Uint8Array) => void;
  restoreKey: () => void;
  clearKey: () => void;
}

const CryptoContext = createContext<CryptoContextValue | null>(null);

export function CryptoProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CryptoState>({
    encryptionKey: null,
    isUnlocked: false,
  });

  const setEncryptionKey = (key: Uint8Array) => {
    persistKeyForSession(key);
    setState({
      encryptionKey: key,
      isUnlocked: true,
    });
  };

  const restoreKey = () => {
    const key = restoreKeyFromSession();
    if (key) {
      setState({
        encryptionKey: key,
        isUnlocked: true,
      });
    }
  };

  const clearKey = () => {
    clearSessionKey();
    setState({
      encryptionKey: null,
      isUnlocked: false,
    });
  };

  return (
    <CryptoContext.Provider value={{ ...state, setEncryptionKey, restoreKey, clearKey }}>
      {children}
    </CryptoContext.Provider>
  );
}

export function useCrypto() {
  const context = useContext(CryptoContext);
  if (!context) {
    throw new Error('useCrypto must be used within CryptoProvider');
  }
  return context;
}
